import { Subject, SubjectSearchResult, isCustomSubject } from '../_interfaces/subject';
import { Visit, visitLocation } from '../_interfaces/visit';
import { dateToDateOnlyLocal } from './dmy-date';

/**
 * The slice of `ApiService` the lookup needs, declared structurally so this helper stays
 * free of service imports and specs can hand it a plain object.
 */
export interface ScheduleVisitLookupApi {
  getSubjects(params: { allSubjects: true, searchQuery?: string, searchLimit?: number }): Promise<SubjectSearchResult>;
  getVisits(patientId: number): Promise<Visit[]>;
}

/**
 * Schedule rows are the only patients that arrive with an admitted slot and a list name; searched
 * patients come from `_patient`, which has neither. The distinction matters because a searched
 * patient reaches encounter creation without a visit only by declining the visit step, and that
 * choice must be respected rather than backfilled.
 */
export function isScheduleRow(
  patient: Subject | { subject_id?: string } | null | undefined
): patient is Subject {
  const row = patient as Subject | null | undefined;
  return !!row?.admit_date && !!row?.patient_list_user;
}

function localDayOf(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : dateToDateOnlyLocal(date);
}

/**
 * Picks the visit a schedule row stands for. A patient list row carries its visit number in
 * `subject_alternative_id` — the pair hoffweb-api matches on when it links a task back to the
 * list — so that is the primary key. Tenants that leave it unpopulated fall back to the visit
 * admitted on the same day as the scheduled slot.
 */
export function matchScheduleVisit(visits: Visit[], patient: Subject): Visit | null {
  if (!visits?.length) return null;

  const visitNumber = patient.subject_alternative_id?.trim();
  if (visitNumber) {
    const byNumber = visits.find(v => v.visit_number?.trim() === visitNumber);
    if (byNumber) return byNumber;
  }

  const admitDay = localDayOf(patient.admit_date);
  if (admitDay) {
    const byDay = visits.find(v => localDayOf(v.visit_admit_date_time) === admitDay);
    if (byDay) return byDay;
  }

  return null;
}

/**
 * Looks up the HL7 visit a schedule row stands for. Failures resolve to null rather than
 * propagating: tenants with `disablePatientSearch` answer the search with a 400, and that
 * must not stop a consultation from starting.
 */
export async function resolveScheduleVisit(
  api: ScheduleVisitLookupApi,
  patient: Subject
): Promise<Visit | null> {
  const subjectId = patient?.subject_id?.trim();
  if (!subjectId || isCustomSubject(patient)) return null;

  try {
    const search = await api.getSubjects({ allSubjects: true, searchQuery: subjectId, searchLimit: 5 });
    const hl7Patient = (search?.rows ?? []).find(
      row => row.subject_id?.trim() === subjectId && Number(row.id) > 0
    );
    if (!hl7Patient) return null;

    const visits = await api.getVisits(hl7Patient.id);
    return matchScheduleVisit(visits ?? [], patient);
  } catch {
    return null;
  }
}

/**
 * Resolves the location of a scheduled patient's visit.
 *
 * The schedule endpoint returns no location, and its rows expose their `_patient_list` id rather
 * than the HL7 `_patient.id` the visits endpoint is keyed on, so the patient is looked up by MRN
 * first. Location is presentational, so every failure resolves to null rather than propagating:
 * tenants with `disablePatientSearch` answer the search with a 400, and that must not stop a
 * consultation from starting.
 */
export async function resolveScheduleVisitLocation(
  api: ScheduleVisitLookupApi,
  patient: Subject
): Promise<string | null> {
  return visitLocation(await resolveScheduleVisit(api, patient));
}
